import { useEscapeKey } from '../../hooks/useEscapeKey.ts';
import React, { useState, useEffect } from 'react';
import {
  X,
  Calendar,
  Clock,
  Repeat,
  Users,
  Link2,
  ClipboardList,
  Check,
  Sparkles,
  Plus,
} from 'lucide-react';
import { Space, SpaceMember } from '../../context/TenantContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';

interface ScheduleTeamMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Space;
  initialOneOnOneMember?: SpaceMember | null;
  teamTasks?: any[];
  onScheduled?: (meeting: any) => void;
}

export const ScheduleTeamMeetingModal: React.FC<ScheduleTeamMeetingModalProps> = ({
  isOpen,
  onClose,
  team,
  initialOneOnOneMember = null,
  teamTasks = [],
  onScheduled,
}) => {
  const { user, token } = useAuth();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('10:00');
  const [durationMinutes, setDurationMinutes] = useState<number>(30);
  const [recurrence, setRecurrence] = useState<'once' | 'daily' | 'weekly'>('once');
  const [selectedAttendees, setSelectedAttendees] = useState<SpaceMember[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [selectedTaskCodes, setSelectedTaskCodes] = useState<string[]>([]);
  const [meetingUrl, setMeetingUrl] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);

    const tomorrow = new Date(Date.now() + 86400000);
    setDateStr(tomorrow.toISOString().slice(0, 10));
    setTimeStr('10:00');
    setDurationMinutes(30);
    setRecurrence('once');

    const roomSlug = (team.slug || team.name || 'team')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    setMeetingUrl(
      `https://meet.ihbdigital.com/${roomSlug}-${Math.random().toString(36).substring(2, 6)}`
    );

    if (initialOneOnOneMember) {
      setTitle(`1-on-1 Sync: ${user?.fullName?.split(' ')[0] || 'Host'} & ${initialOneOnOneMember.fullName}`);
      setDescription(`1-on-1 check-in and task alignment inside ${team.name} team.`);
      setSelectedAttendees([initialOneOnOneMember]);
    } else {
      setTitle(`${team.name} Team Sync / Sprint Review`);
      setDescription(`Team sync for @${team.slug || team.name.toLowerCase()} board tasks and deliverables.`);
      setSelectedAttendees(team.members || []);
    }

    if (team.boards && team.boards.length > 0) {
      setSelectedBoardId(team.boards[0].id);
    } else {
      setSelectedBoardId('');
    }
    setSelectedTaskCodes([]);
  }, [isOpen, team, initialOneOnOneMember, user?.fullName]);

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  // Filter agenda candidate tasks: prioritize 'in_review' and 'overdue' tasks
  const todayStr = new Date().toISOString().slice(0, 10);
  const agendaCandidates = teamTasks.filter((t) => {
    const isOverdue =
      t.dueDate && String(t.dueDate).slice(0, 10) < todayStr && t.status !== 'done';
    return t.status === 'in_review' || isOverdue || t.status === 'in_progress';
  });

  const toggleAttendee = (member: SpaceMember) => {
    setSelectedAttendees((prev) =>
      prev.some((a) => a.id === member.id)
        ? prev.filter((a) => a.id !== member.id)
        : [...prev, member]
    );
  };

  const toggleTaskCode = (code: string) => {
    setSelectedTaskCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !dateStr || !timeStr) {
      setError('Meeting title, date, and start time are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const scheduledAt = new Date(`${dateStr}T${timeStr}:00`).toISOString();

      const res = await fetch('/api/meetings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          clientName: `${team.name} Team`,
          teamId: team.id,
          projectId: selectedBoardId || null,
          scheduledAt,
          durationMinutes,
          recurrence,
          linkedTaskIds: selectedTaskCodes,
          meetingUrl: meetingUrl.trim(),
          attendees: selectedAttendees.map((m) => ({
            id: m.id,
            name: m.fullName,
            email: m.email,
            role: m.spaceRole === 'lead' ? 'Team Lead' : m.title || 'Member',
            avatarUrl: m.avatarUrl || undefined,
          })),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to schedule meeting');
      }

      if (onScheduled) onScheduled(data.meeting);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to schedule team meeting');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 rounded-3xl w-full max-w-xl shadow-modal overflow-hidden my-auto max-h-[92vh] flex flex-col animate-in fade-in zoom-in-95 duration-150"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-surface-200 dark:border-surface-800 bg-gradient-to-r from-[#0162fc] via-[#2350fa] to-[#4b2efa] text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold">
                {initialOneOnOneMember
                  ? `Schedule 1-on-1 with ${initialOneOnOneMember.fullName}`
                  : `Schedule ${team.name} Team Meeting`}
              </h2>
              <p className="text-[11px] text-white/80">
                Posts meeting card to #{team.slug} chat & notifies attendees
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/15 text-white/80 hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-500 text-xs font-semibold">
              {error}
            </div>
          )}

          {/* Auto-populated Team & Meeting Title */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1">
                Team
              </label>
              <div className="px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-bold text-surface-900 dark:text-surface-100 flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: team.color || '#0162fc' }}
                />
                <span className="truncate">{team.name}</span>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1">
                Meeting Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`${team.name} Team Sync / Sprint Review`}
                className="w-full px-3.5 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          {/* Date, Start Time, Duration */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1">
                Date <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                required
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs text-surface-900 dark:text-surface-100"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1">
                Start Time <span className="text-rose-500">*</span>
              </label>
              <input
                type="time"
                required
                value={timeStr}
                onChange={(e) => setTimeStr(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs text-surface-900 dark:text-surface-100"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1">
                Duration
              </label>
              <div className="grid grid-cols-4 gap-1">
                {[15, 30, 45, 60].map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    onClick={() => setDurationMinutes(mins)}
                    className={`py-2 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                      durationMinutes === mins
                        ? 'bg-brand-600 text-white shadow-2xs'
                        : 'bg-surface-50 dark:bg-surface-800 text-surface-600 dark:text-surface-400 border border-surface-200 dark:border-surface-700 hover:bg-surface-100'
                    }`}
                  >
                    {mins === 60 ? '1h' : `${mins}m`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Recurrence */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1.5">
              <Repeat className="w-3 h-3 inline mr-1" />
              Recurrence
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'once', label: 'One-time' },
                { id: 'daily', label: 'Daily Standup' },
                { id: 'weekly', label: 'Weekly Sync' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setRecurrence(opt.id as any)}
                  className={`py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    recurrence === opt.id
                      ? 'bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-300 border border-brand-300 dark:border-brand-700'
                      : 'bg-surface-50 dark:bg-surface-800 text-surface-600 dark:text-surface-400 border border-surface-200 dark:border-surface-700 hover:bg-surface-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Attendees Chip List */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500">
                <Users className="w-3 h-3 inline mr-1" />
                Team Attendees ({selectedAttendees.length})
              </label>
              <button
                type="button"
                onClick={() => setSelectedAttendees(team.members || [])}
                className="text-[10px] font-bold text-brand-600 dark:text-brand-400 hover:underline cursor-pointer"
              >
                Select All Team Members
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 p-2.5 rounded-xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200/60 dark:border-surface-700/60 max-h-28 overflow-y-auto">
              {(team.members || []).map((m) => {
                const isSelected = selectedAttendees.some((a) => a.id === m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleAttendee(m)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-brand-600 text-white shadow-2xs'
                        : 'bg-white dark:bg-surface-800 text-surface-600 dark:text-surface-400 border border-surface-200 dark:border-surface-700 hover:bg-surface-100'
                    }`}
                  >
                    <span>{m.fullName}</span>
                    {isSelected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Linked Board / Agenda Tasks ("In Review" or "Overdue") */}
          {agendaCandidates.length > 0 && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1.5">
                <ClipboardList className="w-3 h-3 inline mr-1" />
                Attach "In Review" / "Overdue" Tasks to Agenda (Optional)
              </label>
              <div className="max-h-28 overflow-y-auto rounded-xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200/60 dark:border-surface-700/60 p-2 space-y-1">
                {agendaCandidates.slice(0, 12).map((t) => {
                  const code = t.code || t.id;
                  const isChecked = selectedTaskCodes.includes(code);
                  return (
                    <div
                      key={t.id}
                      onClick={() => toggleTaskCode(code)}
                      className={`px-2.5 py-1.5 rounded-lg flex items-center justify-between gap-2 text-xs cursor-pointer transition-colors ${
                        isChecked
                          ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300'
                          : 'hover:bg-white dark:hover:bg-surface-800 text-surface-700 dark:text-surface-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono font-bold text-brand-600 dark:text-brand-400 text-[11px] shrink-0">
                          {code}
                        </span>
                        <span className="truncate">{t.title}</span>
                      </div>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shrink-0 ${
                          t.status === 'in_review'
                            ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                            : 'bg-rose-500/15 text-rose-600 dark:text-rose-400'
                        }`}
                      >
                        {String(t.status || '').replace('_', ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Meeting Link / Room */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-surface-500 mb-1">
              <Link2 className="w-3 h-3 inline mr-1" />
              Meeting Link / Call Room
            </label>
            <input
              type="text"
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
              placeholder="https://meet.ihbdigital.com/..."
              className="w-full px-3.5 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-mono text-surface-900 dark:text-surface-100 placeholder-surface-400"
            />
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-surface-200 dark:border-surface-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !title.trim()}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold shadow-sm disabled:opacity-50 cursor-pointer transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>{isSubmitting ? 'Scheduling...' : 'Schedule Team Meeting'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleTeamMeetingModal;
